import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Skeleton } from "components/ui/skeleton";
import { USAGE_LIMITS_I18N } from "features/usage-config/constants/usage-meters";
import { cn } from "lib/utils";

const T = USAGE_LIMITS_I18N;

export interface UsageScopeListItem {
  id: string;
  name: string;
  sub?: string;
  /** Whether this row keeps settings of its own rather than following what it inherits. */
  isCustom: boolean;
}

export interface UsageScopeListProps {
  items: UsageScopeListItem[];
  total: number;
  selectedId: string;
  onSelect: (id: string) => void;
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  inheritedLabel: string;
  isLoading?: boolean;
  /** The organization filter, on the members view. */
  filter?: ReactNode;
}

export const UsageScopeList = ({
  items,
  total,
  selectedId,
  onSelect,
  search,
  onSearch,
  searchPlaceholder,
  inheritedLabel,
  isLoading,
  filter,
}: UsageScopeListProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex max-h-72 w-full flex-col rounded-sm border bg-card lg:h-full lg:max-h-none lg:w-80 lg:shrink-0">
      <div className="flex flex-col gap-2 border-b p-3">
        <Input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />
        {filter}
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto p-2">
        {isLoading &&
          Array.from({ length: 4 }).map((_item, index) => (
            <Skeleton key={index} className="mb-2 h-12 w-full" />
          ))}

        {!isLoading && !items.length && (
          <li className="p-4 text-center text-xs text-muted-foreground">{t(T.NOTHING_FOUND)}</li>
        )}

        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onSelect(item.id)}
              aria-current={item.id === selectedId}
              className={cn(
                "mb-1 flex w-full flex-col gap-1 rounded-sm px-3 py-2 text-left hover:bg-muted",
                item.id === selectedId && "bg-muted",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{item.name}</span>
                <Badge variant={item.isCustom ? "secondary" : "outline"} className="shrink-0">
                  {item.isCustom ? t(T.CHIP_CUSTOM) : inheritedLabel}
                </Badge>
              </span>
              {item.sub && (
                <span className="truncate text-xs text-muted-foreground">{item.sub}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="border-t px-3 py-2 text-xs text-muted-foreground">
        {t(T.SHOWING_COUNT, { shown: items.length, total })}
      </div>
    </div>
  );
};
